/**
 * Provisioning → Packs tab. The section's landing view — packs are the
 * primary surface for composing and assigning feature configuration.
 *
 * Feature pack management for administrators.
 * Lists packs with create/copy/delete actions; clicking a row opens the detail view.
 */

import { useState, type ReactElement } from 'react';
import {
  useAdminPacks,
  useCreatePack,
  useDeletePack,
  useCopyPack,
} from '@features/devcontainer/hooks/useAdminPacks';
import { PackDetailView } from '@features/devcontainer/components/admin/PackDetailView';
import { CreatePackDialog } from '@features/devcontainer/components/admin/CreatePackDialog';
import type { FeaturePackSummary } from '@features/devcontainer/schemas/admin.schemas';

export function PacksTab(): ReactElement {
  const { data: packs, isLoading, error } = useAdminPacks();
  const createPack = useCreatePack();
  const deletePack = useDeletePack();
  const copyPack = useCopyPack();

  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCreate = (name: string, displayName: string, description: string): void => {
    createPack.mutate(
      { name, displayName, ...(description ? { description } : {}) },
      { onSuccess: () => { setDialogOpen(false); createPack.reset(); } },
    );
  };

  const handleDialogClose = (): void => {
    setDialogOpen(false);
    createPack.reset();
  };

  const handleDelete = (pack: FeaturePackSummary): void => {
    if (pack.linked_project_count > 0) return;
    if (!window.confirm(`Delete "${pack.display_name}"?`)) return;
    deletePack.mutate(pack.id);
  };

  const handleCopy = (pack: FeaturePackSummary): void => {
    copyPack.mutate({
      packId: pack.id,
      name: `${pack.name}-copy`,
      displayName: `${pack.display_name} (Copy)`,
    });
  };

  if (selectedPackId) {
    return <PackDetailView packId={selectedPackId} onBack={() => { setSelectedPackId(null); }} />;
  }

  if (isLoading) {
    return <div className="settings-loading">Loading feature packs...</div>;
  }

  if (error) {
    return <div className="settings-error">Failed to load feature packs. Please refresh the page.</div>;
  }

  return (
    <>
      <div className="admin-page__header">
        <button
          type="button"
          className="admin-page__create-btn"
          onClick={() => { setDialogOpen(true); }}
        >
          Create Pack
        </button>
      </div>

      <table className="feature-table__table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Features</th>
            <th>Projects</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(packs ?? []).length === 0 ? (
            <tr>
              <td colSpan={5} className="feature-table__empty">No feature packs created yet.</td>
            </tr>
          ) : (
            (packs ?? []).map((pack) => (
              <tr key={pack.id} onClick={() => { setSelectedPackId(pack.id); }} style={{ cursor: 'pointer' }}>
                <td className="feature-table__name">{pack.display_name}</td>
                <td>{pack.description ?? ''}</td>
                <td>{String(pack.item_count)}</td>
                <td>{String(pack.linked_project_count)}</td>
                <td>
                  <div className="admin-page__actions" onClick={(e) => { e.stopPropagation(); }}>
                    <button type="button" className="feature-table__edit-btn"
                      onClick={() => { setSelectedPackId(pack.id); }}>Edit</button>
                    <button type="button" className="feature-table__edit-btn"
                      onClick={() => { handleCopy(pack); }}>Copy</button>
                    <button type="button" className="feature-table__edit-btn feature-table__edit-btn--danger"
                      onClick={() => { handleDelete(pack); }}
                      disabled={pack.linked_project_count > 0}
                      title={pack.linked_project_count > 0 ? 'Unlink all projects before deleting' : 'Delete pack'}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <CreatePackDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleCreate}
        isSubmitting={createPack.isPending}
        error={createPack.isError ? 'Failed to create pack. A pack with this name may already exist.' : null}
      />
    </>
  );
}
